/* Port of backend/schema/structs/*.dart — the in-memory structs (they never
 * touch Firestore in this app; getDirections' response is mapped into them).
 *
 * Dart getters null-coalesce: text => _text ?? '', value => _value ?? 0,
 * distance => _distance ?? DistanceModelStruct(). The `?? 0` on value is
 * LOAD-BEARING: the map's walk-mode gate is `distance.value < 20000`, so an
 * EMPTY model passes the gate (0 < 20000) — circles, not polyline. Use the
 * accessors below, never raw field reads.
 *
 * NOTE: DirectionModelStruct has NO polyline field. The polyline from the
 * cloud function response lives only in CustomGMap's _routePoints. */

export interface LatLng {
  lat: number
  lng: number
}

export interface DistanceModel {
  text?: string | null
  value?: number | null
}

export interface DurationModel {
  text?: string | null
  value?: number | null
}

export interface DirectionModel {
  distance?: DistanceModel | null
  duration?: DurationModel | null
}

export interface PointLocation {
  createdAt?: Date | null
  location?: LatLng | null
}

/* ---- Dart-getter accessors ---------------------------------------------- */

/** DirectionModelStruct.distance getter: `_distance ?? DistanceModelStruct()`. */
export function dirDistance(m: DirectionModel | null | undefined): {
  text: string
  value: number
} {
  return {
    text: m?.distance?.text ?? '',
    value: m?.distance?.value ?? 0,
  }
}

/** DirectionModelStruct.duration getter. */
export function dirDuration(m: DirectionModel | null | undefined): {
  text: string
  value: number
} {
  return {
    text: m?.duration?.text ?? '',
    value: m?.duration?.value ?? 0,
  }
}

/** DirectionModelStruct.fromMap over the getDirections response. Extra keys
 * (e.g. `polyline`) are intentionally NOT stored — same as the Dart struct. */
export function directionModelFromMap(data: {
  distance?: { text?: string; value?: number } | null
  duration?: { text?: string; value?: number } | null
}): DirectionModel {
  return {
    distance: data.distance ?? null,
    duration: data.duration ?? null,
  }
}
